/**
 * The field's phones — who has the app, who is using it, who is a rumor.
 *
 * Built for the man running the week: four days out, the question is not
 * "who is winning" but "who has never signed in and will be doing paper math
 * on Sunday". One row per man: last seen, opens, minutes, where the minutes
 * went, and whether his phone can be buzzed about a birdie. The men with no
 * trace at all float to the top in the signal color — they are the to-do
 * list.
 *
 * Renders only for roster admins; the server enforces the same gate.
 */

import { useEffect, useState } from 'react'
import { formatMoney } from '../engine/odds'
import type { Tournament } from '../engine/types'
import { playerName, todayLocal } from '../select'
import { call, useStore, type Book } from '../store'
import { SectionRule } from '../ui'

interface UsageRow {
  player_id: string
  day: string
  screen: string
  opens: number
  seconds: number
  last_seen: string
}

interface Payload {
  usage: UsageRow[]
  push: { player_id: string; devices: number; failures: number }[]
  lastScore: { entered_by: string; last: string }[]
}

const ago = (iso: string): string => {
  const mins = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000))
  if (mins < 60) return `${mins}m ago`
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`
  return `${Math.round(mins / (60 * 24))}d ago`
}

export function Phones({ doc }: { doc: Tournament }) {
  const me = useStore((s) => s.session?.playerId ?? '')
  const [data, setData] = useState<Payload | null>(null)
  /*
   * The book, for the other half of "is he actually using this" — a man who
   * has not opened the app but has three slips down is a different problem
   * from a man nobody has heard from at all. Fetched here rather than folded
   * into /api/usage: every phone may already read every slip, so this is a
   * second read of something public, not a new privilege.
   */
  const [book, setBook] = useState<Book | null>(null)
  const [error, setError] = useState<string | null>(null)

  const amAdmin = doc.players.find((p) => p.id === me)?.isAdmin ?? false

  useEffect(() => {
    if (!amAdmin) return
    let live = true
    call<Payload>('usage')
      .then((d) => live && setData(d))
      .catch((e) => live && setError(e instanceof Error ? e.message : 'Could not load'))
    call<Book>('odds')
      .then((b) => live && setBook(b))
      .catch(() => {})
    return () => {
      live = false
    }
  }, [amAdmin])

  if (!amAdmin) return null

  const today = todayLocal()
  const push = new Map((data?.push ?? []).map((r) => [r.player_id, r]))
  const lastScore = new Map((data?.lastScore ?? []).map((r) => [r.entered_by, r.last]))

  const perPlayer = new Map<
    string,
    { opens: number; seconds: number; lastSeen: string; today: number; screens: Map<string, number> }
  >()
  for (const r of data?.usage ?? []) {
    const p =
      perPlayer.get(r.player_id) ??
      { opens: 0, seconds: 0, lastSeen: '', today: 0, screens: new Map<string, number>() }
    p.opens += r.opens
    p.seconds += r.seconds
    if (r.day === today) p.today += r.seconds
    if (r.last_seen > p.lastSeen) p.lastSeen = r.last_seen
    if (r.screen !== 'app') p.screens.set(r.screen, (p.screens.get(r.screen) ?? 0) + r.seconds)
    perPlayer.set(r.player_id, p)
  }

  /** Slips per man: how many, how much, and how much has not landed. */
  const betting = new Map<string, { n: number; down: number; owed: number }>()
  for (const b of book?.bets ?? []) {
    if (b.status === 'void') continue
    const cur = betting.get(b.player_id) ?? { n: 0, down: 0, owed: 0 }
    cur.n += 1
    cur.down += b.stake_cents
    if (b.status === 'pending') cur.owed += b.stake_cents
    betting.set(b.player_id, cur)
  }

  /*
   * Everybody with a login, not just the men in the draw — Scott's ask.
   * Tyler plays no golf and is `active: false` so the pairings leave him
   * alone, but he has a phone, an account and money on the board, and this
   * panel exists to answer "is everyone actually using the thing".
   * Excluding him hid the one man whose only reason to open it is betting.
   */
  const rows = doc.players
    .filter((p) => p.active || perPlayer.has(p.id) || betting.has(p.id))
    .map((p) => ({ player: p, u: perPlayer.get(p.id) ?? null }))
    .sort((a, b) => {
      if (!a.u !== !b.u) return a.u ? 1 : -1
      return (b.u?.lastSeen ?? '').localeCompare(a.u?.lastSeen ?? '')
    })
  // Ghosts first is the point; among the seen, most recent first.
  rows.sort((a, b) => (a.u ? 1 : 0) - (b.u ? 1 : 0))

  const seen = rows.filter((r) => r.u).length

  return (
    <>
      <SectionRule>The field&rsquo;s phones</SectionRule>
      <p className="px-4 pt-1 pb-2 text-[13px] text-mute leading-snug">
        {seen} of {rows.length} have opened the app
        {rows.some((r) => !r.player.active) &&
          `, ${rows.filter((r) => !r.player.active).length} of them betting only`}
        .{seen < rows.length && ' The men in red are doing paper math on Sunday.'}
        {betting.size > 0 && (
          <>
            {' '}
            {betting.size} {betting.size === 1 ? 'has' : 'have'} bet —{' '}
            {formatMoney(
              [...betting.values()].reduce((n, b) => n + b.down, 0),
            )}{' '}
            down, {formatMoney([...betting.values()].reduce((n, b) => n + b.owed, 0))}{' '}
            of it still owed.
          </>
        )}
      </p>
      {error && <p className="px-4 pb-2 text-[13px] text-signal">{error}</p>}
      <ul className="divide-y divide-rule border-t border-b border-rule">
        {rows.map(({ player, u }) => {
          const pu = push.get(player.id)
          const top = u
            ? [...u.screens.entries()].sort((x, y) => y[1] - x[1]).slice(0, 2)
            : []
          return (
            <li key={player.id} className="px-4 py-2.5">
              <div className="flex items-baseline justify-between gap-3">
                <span className="min-w-0">
                  <span className={`bc-name text-sm ${u ? '' : 'text-signal'}`}>
                    {playerName(doc, player.id)}
                  </span>
                  {/* A login without a card. He is here for the book, so the
                      "N of 12 have opened it" count stays about the field. */}
                  {!player.active && (
                    <span className="bc-chip bc-label bg-wash text-mute px-1.5 ml-1.5">
                      Betting only
                    </span>
                  )}
                </span>
                <span className="text-[12px] text-mute shrink-0">
                  {u ? `seen ${ago(u.lastSeen)}` : 'never signed in'}
                </span>
              </div>
              <div className="text-[12px] text-mute mt-0.5 leading-snug">
                {u ? (
                  <>
                    {u.opens} open{u.opens === 1 ? '' : 's'} ·{' '}
                    {Math.round(u.seconds / 60)} min
                    {u.today > 0 && ` (${Math.round(u.today / 60)} today)`}
                    {top.length > 0 &&
                      ` · mostly ${top.map(([k, v]) => `${k} ${Math.round(v / 60)}m`).join(', ')}`}
                  </>
                ) : (
                  'No opens, no scores, no trace.'
                )}
                {' · '}
                {pu ? (
                  <span>
                    alerts on ({pu.devices} device{pu.devices === 1 ? '' : 's'})
                    {pu.failures > 0 && <span className="text-signal"> ⚠ failing</span>}
                  </span>
                ) : (
                  'alerts off'
                )}
                {lastScore.has(player.id) &&
                  ` · last scored ${ago(lastScore.get(player.id)!)}`}
              </div>
              {(() => {
                const b = betting.get(player.id)
                if (!b) return null
                return (
                  <div className="text-[12px] text-mute leading-snug">
                    {b.n} slip{b.n === 1 ? '' : 's'} · {formatMoney(b.down)} down
                    {b.owed > 0 && (
                      <span className="text-signal">
                        {' '}· {formatMoney(b.owed)} not in yet
                      </span>
                    )}
                  </div>
                )
              })()}
            </li>
          )
        })}
      </ul>
    </>
  )
}
